// @flow

import { Component } from '../jsti/core';
import Directions from '../constants/Directions';
import Notifications from '../constants/Notifications';
import EventNames from '../constants/EventNames';
import { outerHeight } from '../classes/Helpers';

export default class HorizontalSlider extends Component {
  #id: string = '';
  #index: number = 0;
  totalWidth: number = 0;
  containerWidth: number = 0;
  navigation: HTMLElement;
  activeClass: string;
  inActiveClass: string;

  /**
   * Initialize the component
   */
  bind(el: HTMLElement): void {
    super.bind(el);
    this.setUp();

    if (this.items.length) {
      this.updateSliderInformation();
    }

    this.watchItems(this.updateSliderInformation);

    this.addListener(Notifications.SLIDE, this.onSlideNotification);
  }

  /**
   * Validation and setup of properties
   */
  setUp() {
    this.#id = this.el.id;

    if (!this.#id) {
      throw new Error('A slider id is required');
    }

    this.navigations = document.body.getElementsByRef('navigation');
    this.navigation = Array.from(this.navigations).find((navigationElement: HTMLElement) => (navigationElement.dataset.sliderId === this.#id));

    if (this.navigation) {
      this.navigation.addEventListener(EventNames.CLICK, this.onNavigationClick.bind(this));
      const activeClass = this.navigation.dataset.activeClassList;
      if (activeClass) {
        this.activeClass = activeClass;
      }
      const inActiveClass = this.navigation.dataset.inactiveClassList;
      if (inActiveClass) {
        this.inActiveClass = inActiveClass;
      }
    }
  }

  /**
   * Watch the items for changes, executes the callback when the slider changes
   */
  watchItems(callback: function): void {
    new MutationObserver(callback.bind(this)).observe(this.el, { childList: true });
  }

  /**
   * Update the information of the slider
   */
  updateSliderInformation(): void {
    this.totalWidth = this.el.scrollWidth;
    this.containerWidth = this.el.offsetWidth;

    if (this.isSlideable()) {
      this.notify(Notifications.SHOW_RIGHT_SLIDER_BUTTON, { slider: this.#id });
    }
    if (this.navigation) {
      this.renderNavigation();
    }
    this.updateNavigation(this.index);
    const firstSliderItem = this.el.querySelector('.box__outer');
    if (firstSliderItem) {
      const style: any = this.el.currentStyle || window.getComputedStyle(firstSliderItem);
      const margin: number = parseInt(style.marginTop || 0, 10);
      this.notify(Notifications.UPDATE_HORIZONTAL_SLIDER_BUTTON_HEIGHT, { sliderId: this.#id, itemHeight: firstSliderItem.offsetHeight, offset: margin });
    } else {
      this.notify(Notifications.UPDATE_HORIZONTAL_SLIDER_BUTTON_HEIGHT, { sliderId: this.#id, itemHeight: outerHeight(this.el) });
    }
  }

  /**
   * Executes when a slide notification is received
   */
  onSlideNotification({ slider, direction }: Object): void {
    if (slider !== this.#id) {
      return;
    }

    this.slide(direction);
  }

  /**
   * Slide in a given direction
   */
  slide(direction: string): void {
    const steps: number = this.calculateSteps();

    if (direction === Directions.LEFT) {
      this.slideLeft(steps);
    }

    if (direction === Directions.RIGHT) {
      this.slideRight(steps);
    }
  }

  /**
   * Slide to the left
   */
  slideLeft(steps: number): void {
    if (!this.canSlideLeft(steps)) {
      this.reset();
      return;
    }
    this.#index = this.#index - steps;
    this.reposition(this.#index);
    this.updateNavigation(this.#index);
  }

  /**
   * Slide to the right
   */
  slideRight(steps: number): void {
    if (!this.canSlideRight(steps)) {
      return;
    }
    this.#index = this.#index + steps;
    this.reposition(this.#index);
    this.updateNavigation(this.#index);
  }

  /**
   * `Slides` the slider to the offset left of a given index
   */
  reposition(index: number): void {
    const position: number = index === 0
      ? 0
      : this.el.children[index].offsetLeft - parseInt(window.getComputedStyle(this.el).paddingLeft, 10);
    this.el.scrollTo(position, 0);
  }

  /**
   * Reset the slider to its start position
   */
  reset(): void {
    this.reposition(0);
    this.#index = 0;
    this.updateNavigation(this.#index);
  }

  /**
   * Check if the total width of all items are larger than the container
   */
  isSlideable(): boolean {
    return this.totalWidth > this.containerWidth;
  }

  /**
   * Check if the slider is able to slide left
   */
  canSlideLeft(steps: number): boolean {
    return (this.#index - steps) > 0;
  }

  /**
   * Check if the slider is able to slide right
   */
  canSlideRight(steps: number): boolean {
    return (this.#index + steps) < this.el.children.length;
  }

  /**
   * Calculate the amount of steps the slider should slide
   */
  calculateSteps(): number {
    const padding: number = parseInt(window.getComputedStyle(this.el.firstElementChild).paddingRight, 10);
    const averageItemWidth: number = this.totalWidth / this.el.children.length;

    // Need to add padding, because the padding of the last item
    return Math.floor((this.containerWidth + padding) / averageItemWidth);
  }

  /**
   * Set the total width of all items
   */
  setTotalWidth(): void {
    this.totalWidth = this.el.scrollWidth;
  }

  /**
   * Set the width of the container to a property
   */
  setContainerWidth(): void {
    this.containerWidth = this.el.offsetWidth;
  }

  /**
   * Get the index of the item which users see as the "first" item
   */
  get index(): number {
    return this.#index;
  }

  /**
   * Get all the items of the slider
   */
  get items(): Array<HTMLElement> {
    return [...this.el.children].filter((element: HTMLElement) => !(element instanceof HTMLTemplateElement));
  }

  /**
   *
   */
  renderNavigation() {
    while (this.navigation.firstChild) {
      this.navigation.removeChild(this.navigation.firstChild);
    }
    for (let i = 0; i < Math.ceil(this.items.length / this.calculateSteps()); i++) {
      const newNavigation = document.createElement('span');
      newNavigation.id = i;
      newNavigation.classList = 'hover:cursor-pointer w-4 h-4 rounded-full mr-3';
      if (i === 0) {
        newNavigation.classList.add(this.activeClass);
      } else {
        newNavigation.classList.add(this.inActiveClass);
      }
      this.navigation.appendChild(newNavigation);
    }
  }

  /**
   *
   */
  onNavigationClick(event: Event) {
    let index = event.target.id;
    if (!index) return;
    let slideIndex = this.index / this.calculateSteps();

    if (index > slideIndex) {
      while (index > slideIndex) {
        this.slideRight(this.calculateSteps());
        slideIndex++;
      }
    } else if (index < slideIndex) {
      while (index < slideIndex) {
        this.slideLeft(this.calculateSteps());
        index++;
      }
    }
  }

  /**
   *
   */
  updateNavigation(index: number) {
    this.updateSliderButtons();
    if (!this.navigation) return;
    Array.from(this.navigation.children).forEach((navigationElement: HTMLInputElement) => {
      if (Number(navigationElement.id) === index / this.calculateSteps()) {
        navigationElement.classList.add(this.activeClass);
        navigationElement.classList.remove(this.inActiveClass);
      } else {
        navigationElement.classList.remove(this.activeClass);
        navigationElement.classList.add(this.inActiveClass);
      }
    });
  }

  /**
   * updates the slider buttons
   */
  updateSliderButtons() {
    if (this.canSlideRight(this.calculateSteps())) {
      this.notify(Notifications.SHOW_RIGHT_SLIDER_BUTTON, { slider: this.#id });
    } else {
      this.notify(Notifications.HIDE_RIGHT_SLIDER_BUTTON, { slider: this.#id });
    }

    if (this.canSlideLeft(1)) {
      this.notify(Notifications.SHOW_LEFT_SLIDER_BUTTON, { slider: this.#id });
    } else {
      this.notify(Notifications.HIDE_LEFT_SLIDER_BUTTON, { slider: this.#id });
    }
  }
}
